import { useCallback, useEffect, useState } from 'react'
import type { OrgDto } from '@praesto/shared'
import { api } from './api.js'

const STORAGE_KEY = 'praesto.org'

export interface OrgState {
  orgs: OrgDto[] | null
  current: OrgDto | null
  select: (orgId: string) => void
  reload: () => Promise<void>
}

/**
 * Die Organisationen der angemeldeten Person und die gerade gewählte.
 *
 * Die Wahl liegt im Browser, nicht in der Sitzung: Sie ist eine Ansichtssache
 * — welche Boards und Vorlagen in der Übersicht erscheinen — und keine
 * Berechtigung. Der Zugriff auf ein einzelnes Board hängt nie daran, sondern
 * immer nur an der Mitgliedschaft. Deshalb funktioniert ein Board-Link aus
 * einer anderen Organisation ohne vorheriges Umschalten.
 */
export function useOrgs(): OrgState {
  const [orgs, setOrgs] = useState<OrgDto[] | null>(null)
  const [currentId, setCurrentId] = useState<string | null>(
    () => window.localStorage.getItem(STORAGE_KEY),
  )

  const reload = useCallback(async () => {
    const list = await api.orgs()
    setOrgs(list)
    setCurrentId((current) => {
      // Eine gemerkte Organisation, in der man nicht mehr ist, darf nicht
      // stehen bleiben — sonst zeigt die Übersicht dauerhaft einen Fehler.
      if (current && list.some((org) => org.id === current)) return current
      return list[0]?.id ?? null
    })
  }, [])

  useEffect(() => {
    void reload().catch(() => setOrgs([]))
  }, [reload])

  useEffect(() => {
    if (currentId) window.localStorage.setItem(STORAGE_KEY, currentId)
  }, [currentId])

  const current = orgs?.find((org) => org.id === currentId) ?? null
  return { orgs, current, select: setCurrentId, reload }
}
